import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAuth } from 'firebase/auth';
import { logoutHelper } from 'src/helpers/logoutHelper';
import { settings } from '../constants';

let cachedToken: string | null = null;
let tokenExpirationTime: number | null = null;

type LimitError = {
  data: {
    error: string;
    message: string;
  };
};

const onSuccess = (response: AxiosResponse) => response.data;

export const ORGANIZATIONS_PAGE = '/settings/organizations';

export const isLimitError = (error: unknown): boolean => {
  const limitError = error as LimitError;
  if (limitError && limitError?.data) {
    return (
      typeof limitError.data === 'object' &&
      'error' in limitError.data &&
      limitError.data.error === 'LimitError'
    );
  }
  return false;
};

const USER_NOT_FOUND_404_REGEX = /User with ID [a-f0-9-]+ not found/;

/** Captures 403 NO_ORGANIZATION / NO_ACCESS_TO_ORGANIZATION (handled in onError → redirect to organizations). */
export const isNoOrganizationError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError;
    if (
      axiosError.response?.data &&
      typeof axiosError.response.data === 'object'
    ) {
      const data = axiosError.response.data as {
        error?: string;
        message?: string;
      };
      return (
        axiosError.response.status === 403 &&
        (data.error === 'NO_ORGANIZATION' ||
          data.error === 'NO_ACCESS_TO_ORGANIZATION')
      );
    }
  }
  return false;
};

export const isUserInvalidError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError;
    if (
      axiosError.response?.status === 404 &&
      axiosError.response?.data &&
      typeof axiosError.response.data === 'object'
    ) {
      const data = axiosError.response.data as { message?: string };
      return USER_NOT_FOUND_404_REGEX.test(data.message ?? '');
    }
  }
  return false;
};

const onError = (error: AxiosError) => {
  if (error.response) {
    const statusCode = error.response.status;

    if (isLimitError(error.response)) {
      window.location.href = '/billing?error=LimitError';
      return Promise.reject(error.message || 'Limit reached');
    }

    if (statusCode === 401) {
      clearCachedToken();
      logoutHelper();
      return Promise.reject(error.response ?? error);
    }

    if (isNoOrganizationError(error) || isUserInvalidError(error)) {
      if (window.location.pathname !== ORGANIZATIONS_PAGE) {
        window.location.href = ORGANIZATIONS_PAGE;
      }
      return Promise.reject(error);
    }

    if (statusCode >= 400 && statusCode < 500) {
      // 401 = invalid session (guard). 403 NO_ORGANIZATION handled above; other 403 = permission denied.
      console.warn('Client error:', statusCode);
    } else if (statusCode >= 500) {
      console.error('Server error:', statusCode);
    }
  } else if (error.request) {
    console.error('Network or server unreachable:', error.message);
  } else {
    console.error('Unexpected error:', error.message);
  }

  return Promise.reject(error.response || error.message);
};

const getToken = async () => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    console.warn('User not authenticated, redirecting to login');
    logoutHelper();
    throw new Error('User not authenticated');
  }

  const currentTime = Date.now() / 1000;
  if (cachedToken && tokenExpirationTime && currentTime < tokenExpirationTime) {
    return cachedToken;
  }

  try {
    const idTokenResult = await user.getIdTokenResult(true);
    cachedToken = idTokenResult.token;
    tokenExpirationTime = idTokenResult.expirationTime
      ? new Date(idTokenResult.expirationTime).getTime() / 1000
      : null;
    return cachedToken;
  } catch {
    logoutHelper();
  }
};

export const renewToken = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user) {
    const idTokenResult = await user.getIdTokenResult(true);
    cachedToken = idTokenResult.token;
  }
};

export const clearCachedToken = () => {
  cachedToken = null;
  tokenExpirationTime = null;
};

type RequestOptions = AxiosRequestConfig & {
  envId?: string;
  withoutToken?: boolean;
};

export const request = async (url: string, options?: RequestOptions) => {
  try {
    const token = options?.withoutToken ? null : await getToken();
    const { headers, ...optionsWithoutHeaders } = options || {};

    const config: AxiosRequestConfig = {
      baseURL: settings.baseApiUrl,
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        'Content-type': 'application/json',
        'x-env': options?.envId || undefined,
        ...headers,
      },
      ...optionsWithoutHeaders,
      url,
    };

    try {
      return await axios.request(config).then(onSuccess);
    } catch (error) {
      return onError(error as AxiosError);
    }
  } catch (error) {
    const isAuthRedirect =
      error &&
      typeof error === 'object' &&
      'response' in error &&
      (error as AxiosError).response?.status === 401;
    if (
      !isAuthRedirect &&
      !isNoOrganizationError(error) &&
      !isUserInvalidError(error)
    ) {
      console.error('Error making request:', error);
    }
    return Promise.reject(error);
  }
};

export const get = (url: string, options?: RequestOptions) =>
  request(url, {
    ...options,
    method: 'GET',
  });

export const post = (url: string, data: object, options?: RequestOptions) =>
  request(url, {
    ...options,
    method: 'POST',
    data,
  });

export const patch = (url: string, data: object, options?: RequestOptions) =>
  request(url, {
    ...options,
    method: 'PATCH',
    data,
  });

export const put = (url: string, data: object, options?: RequestOptions) =>
  request(url, {
    ...options,
    method: 'PUT',
    data,
  });

export const remove = (url: string, options?: RequestOptions) =>
  request(url, {
    ...options,
    method: 'DELETE',
  });
